import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { AdminRoleRoute } from './AdminRoleRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { StaffShell } from '@/components/layout/StaffShell';
import { LoginPage } from '@/pages/LoginPage';
import { CustomersPage } from '@/features/customers/pages/CustomersPage';
import { OptiHubPage } from '@/features/optihub/pages/OptiHubPage';
import { QuoteGeneratorPage } from '@/features/optihub/quote/pages/QuoteGeneratorPage';
import { StripeQuoteGeneratorPage } from '@/features/optihub/stripe-quote/pages/StripeQuoteGeneratorPage';
import { CustomerShell } from '@/features/customers/pages/CustomerShell';
import { CustomerDevicesPage } from '@/features/customers/pages/CustomerDevicesPage';
import { CustomerFleetsPage } from '@/features/customers/pages/CustomerFleetsPage';
import { CustomerTeamsPage } from '@/features/customers/pages/CustomerTeamsPage';
import { CustomerDocumentsPage } from '@/features/customers/pages/CustomerDocumentsPage';
import { DocumentCenterPage } from '@/features/documents/pages/DocumentCenterPage';
import { DocumentResolvePage } from '@/features/documents/pages/DocumentResolvePage';
import { EmployeePeersPage } from '@/features/network/pages/EmployeePeersPage';
import { SubnetsPage } from '@/features/network/pages/SubnetsPage';
import { ConnectionAuditPage } from '@/features/network/pages/ConnectionAuditPage';
import { AdminUsersPage } from '@/features/admin-users/pages/AdminUsersPage';
import { HardwareProductsPage } from '@/features/hardware-products/pages/HardwareProductsPage';
import { TimeTrackerPage } from '@/features/opticlock/pages/TimeTrackerPage';
import { TimeDashboardPage } from '@/features/opticlock/pages/TimeDashboardPage';
import { ActivityLogPage } from '@/features/activity-log/pages/ActivityLogPage';
import { EnrollLaptopPage } from '@/features/enroll/pages/EnrollLaptopPage';
import { OnboardingPage } from '@/features/onboarding/pages/OnboardingPage';
import { GitlabCallbackPage } from '@/features/onboarding/pages/GitlabCallbackPage';
import { AgentDevicesPage } from '@/features/agent-devices/pages/AgentDevicesPage';
import { TrustCenterPage } from '@/features/trust-center/pages/TrustCenterPage';
import { TroubleshootSessionsPage } from '@/features/troubleshoot/pages/TroubleshootSessionsPage';
import { TroubleshootSessionView } from '@/features/troubleshoot/pages/TroubleshootSessionView';
import { OrderDevicesPage } from '@/features/order-devices/pages/OrderDevicesPage';
import { ShopifyOrdersPage } from '@/features/shopify-orders/pages/ShopifyOrdersPage';
import { DeviceOrdersShell } from '@/features/order-devices/pages/DeviceOrdersShell';
import { DemoCouponsPage } from '@/features/order-devices/pages/DemoCouponsPage';
import { RmaPage } from '@/features/order-devices/pages/RmaPage';
import { DevicesFulfillmentShell } from '@/features/order-devices/pages/DevicesFulfillmentShell';
import { AmazonCouponsPage } from '@/features/order-devices/pages/AmazonCouponsPage';
import { IntlRequestsPage } from '@/features/intl-requests/pages/IntlRequestsPage';
import { IntlAllRequestsPage } from '@/features/intl-all-requests/pages/IntlAllRequestsPage';
import { DunningReviewPage } from '@/features/dunning-review/pages/DunningReviewPage';

/**
 * HomeRedirect — role-aware landing.
 *   root | support → /staff/customers  (can see the customer console)
 *   engineer       → /staff/network/peers
 *   readonly       → /staff/onboarding  (My Account only)
 */
function HomeRedirect() {
  const roles = useAuthStore((s) => s.adminUser?.roles ?? []);
  const has = (r: string) => roles.includes(r as (typeof roles)[0]);
  if (has('root') || has('support')) return <Navigate to="/staff/customers" replace />;
  if (has('engineer')) return <Navigate to="/staff/network/peers" replace />;
  // Fulfillment reaches exactly one page, so /staff has to land there — the default below would
  // drop them on Onboarding, and every other destination refuses them.
  if (has('fulfillment')) return <Navigate to="/staff/devices-fulfillment" replace />;
  // Billing reaches exactly one page too.
  if (has('billing')) return <Navigate to="/staff/dunning-review" replace />;
  return <Navigate to="/staff/onboarding" replace />;
}

const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    children: [
      // Root → /staff
      { index: true, element: <Navigate to="/staff" replace /> },

      // Public: login page
      { path: 'staff/login', element: <LoginPage /> },

      // Authenticated: needs Firebase token + staffMe approval
      {
        element: <ProtectedRoute />,
        children: [
          // GitLab OAuth return target (GITLAB_OAUTH_REDIRECT_URI). Firebase-authed
          // (so the bearer token is available) but outside the staff shell.
          { path: 'onboarding/gitlab/cb', element: <GitlabCallbackPage /> },
          {
            path: 'staff',
            element: <AdminRoleRoute />,
            children: [
              {
                element: <StaffShell />,
                children: [
                  // /staff → role-aware landing (console → customers; member → enroll)
                  { index: true, element: <HomeRedirect /> },

                  // Customers + OptiHub — root and support only.
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'support']} />,
                    children: [
                      { path: 'customers', element: <CustomersPage /> },
                      {
                        path: 'customers/:accountId',
                        element: <CustomerShell />,
                        children: [
                          { index: true, element: <Navigate to="devices" replace /> },
                          { path: 'devices', element: <CustomerDevicesPage /> },
                          { path: 'fleets', element: <CustomerFleetsPage /> },
                          { path: 'teams', element: <CustomerTeamsPage /> },
                        ],
                      },
                      // Customer documents — a STANDALONE page, not a customer-360 tab:
                      // uploading, type filters and version history are a workflow, not a
                      // panel. Reached from the Documents button on the customer card.
                      {
                        path: 'customers/:accountId/documents',
                        element: <CustomerDocumentsPage />,
                      },
                      // Durable single-document link, for pointing AT a document from outside
                      // the console (Apply Coupon writes it into Stripe customer metadata).
                      // Mints a fresh presigned URL per click and redirects — so the stored
                      // link never expires and is never itself a credential.
                      {
                        path: 'documents/:documentId',
                        element: <DocumentResolvePage />,
                      },
                      // Document Center — cross-customer lookup. root+support only, matching
                      // the /documents API; the Order Devices group is wider (adds engineer).
                      { path: 'document-center', element: <DocumentCenterPage /> },
                      // OptiClock entry page — root + support, the same tier as Document
                      // Center. The root-only dashboard is a SIBLING route below, never a
                      // child of this one: as a child, this group's guard would be the
                      // effective gate and support would reach the root-only shell.
                      { path: 'opticlock', element: <TimeTrackerPage /> },
                      // A sibling, not a child: OptiHubPage renders tabs with no
                      // <Outlet>, and the quote is a full-page printable document.
                      { path: 'optihub/quote-generator', element: <QuoteGeneratorPage /> },
                      { path: 'optihub/stripe-quote-generator', element: <StripeQuoteGeneratorPage /> },
                    ],
                  },

                  // OptiHub — root + support, plus BILLING (2026-09-23), which OptiHubPage
                  // narrows to the Billing Console's Dunning control alone. Its own guard
                  // because the group it used to sit in is root + support only.
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'support', 'billing']} />,
                    children: [{ path: 'optihub', element: <OptiHubPage /> }],
                  },
                  // Dunning Review — the past-due ladder's terminal rung, plus the Billing
                  // Console's "Dunning Exclusion List" list. ROOT + BILLING only (requester,
                  // 2026-09-23), in its OWN guard because no other tier here includes billing.
                  // The server decides the same rule (dunningReviewCanDecide) for the data;
                  // this guard only stops the page rendering for anyone else.
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'billing']} />,
                    children: [{ path: 'dunning-review', element: <DunningReviewPage /> }],
                  },
                  // Trust Center + Network — role ≥ support (root, engineer, support).
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'engineer', 'support']} />,
                    children: [
                      { path: 'trust-center', element: <TrustCenterPage /> },
                      {
                        path: 'network',
                        children: [
                          { index: true, element: <Navigate to="peers" replace /> },
                          { path: 'peers', element: <EmployeePeersPage /> },
                          { path: 'subnets', element: <SubnetsPage /> },
                        ],
                      },
                      // Troubleshoot (console tier) — cross-tenant session list + read-only transcript.
                      { path: 'troubleshoot', element: <TroubleshootSessionsPage /> },
                      { path: 'troubleshoot/:sessionId', element: <TroubleshootSessionView /> },
                    ],
                  },

                  // Devices Fulfillment — devices sent without a sale. The RMA queue is the INDEX
                  // route and Demo Coupons has its own named URL, `create-demo-coupon`
                  // (requester, 2026-08-25), so tab order and route order agree.
                  //
                  // Both superseded URLs are kept as redirects below rather than dropped: `rma`
                  // (which shipped earlier the same day) and `order-devices/demo`, whose target
                  // had to be RE-POINTED — left aimed at the section root it would have quietly
                  // opened the RMA queue for someone who bookmarked the demo card.
                  //
                  // OUTSIDE the troubleshoot-tier block above, not inside it, and that placement
                  // is the whole point: nested there, its parent guard (root/engineer/support)
                  // would refuse a `fulfillment` user before this guard ever ran — the one role
                  // created for this page would be the one role that could not open it.
                  //
                  // Its own pair is narrower than its neighbours' on purpose, for two different
                  // reasons now. Issuing a demo coupon mints spendable money with no invoice and
                  // no approver, so who holds the role is the control. RMA spends nothing, but
                  // every row is a customer's name, email, postal address and phone — the most
                  // personally-identifying ledger in the app — so its READ is gated just as
                  // tightly. DemoCouponsResolver and RmaResolver enforce the same two roles
                  // server-side; this is the UX half.
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'fulfillment']} />,
                    children: [
                      {
                        path: 'devices-fulfillment',
                        element: <DevicesFulfillmentShell />,
                        children: [
                          { index: true, element: <RmaPage /> },
                          { path: 'create-demo-coupon', element: <DemoCouponsPage /> },
                          // Third tab. Shares this guard pair: allocating an Amazon code is the
                          // same order of trust as issuing a demo coupon. The IMPORT inside it is
                          // root-only, enforced by the resolver and mirrored by the page.
                          { path: 'amazon-coupons', element: <AmazonCouponsPage /> },
                          // Superseded same-day; kept so a link shared in the hours it existed
                          // still lands on the queue rather than a 404.
                          {
                            path: 'rma',
                            element: <Navigate to="/staff/devices-fulfillment" replace />,
                          },
                        ],
                      },
                    ],
                  },
                  // Devices Order — ONE sidebar entry, THREE sections. The GROUP is
                  // root/engineer/support, which is the tier the whole entry had before
                  // International Requests existed and the tier it has again.
                  //
                  // IT WAS BRIEFLY A UNION of root/engineer/support/FULFILLMENT, so a fulfilment
                  // user could reach International Requests. That was wrong in a way worth
                  // recording, because the guard was not the part that was wrong: the sidebar
                  // entry points at the BARE path, whose index is the coupon queue, so a
                  // fulfilment user admitted at the group was then refused by the index child —
                  // a nav entry that only ever led to "not authorized". `demo-coupons-access.spec.ts`
                  // asserts exactly that and caught it on qa. Admitting a role to a parent whose
                  // index it cannot use needs a DESTINATION for that role, not a wider gate.
                  //
                  // International Requests is now ROOT-ONLY (requester, 2026-09-15), so the union
                  // has nothing left to express and the child gate below is the narrowest here.
                  // `engineer` reaches the first two tabs and NOT the third: every international
                  // row is a named customer's company, contact, email, postal address and phone,
                  // and none of it is an engineering diagnostic. `DeviceOrdersShell` filters its
                  // tab bar on the same lists so nobody is shown a tab that refuses them, and
                  // IntlRequestsResolver is root-only on every op server-side. This is the UX half.
                  {
                    element: <AdminRoleRoute allowedRoles={['root', 'engineer', 'support']} />,
                    children: [
                      {
                        path: 'order-devices',
                        element: <DeviceOrdersShell />,
                        children: [
                          // The coupon queue is the INDEX route so /staff/order-devices still
                          // renders it with no redirect (eight e2e specs navigate straight to
                          // that path).
                          { index: true, element: <OrderDevicesPage /> },
                          { path: 'shopify', element: <ShopifyOrdersPage /> },
                          {
                            // ROOT + SUPPORT + FULFILLMENT since 2026-09-21 — support answer
                            // "where is my order" and were routing every one through a root user,
                            // and fulfilment could not reach a request id at all. The resolver's
                            // per-op tiers are the enforcement; this is the UX half, and it is
                            // deliberately the WIDEST of the three (a support user lands on a page
                            // whose write actions refuse them, which is correct — they are here to
                            // read).
                            element: (
                              <AdminRoleRoute allowedRoles={['root', 'support', 'fulfillment']} />
                            ),
                            children: [
                              { path: 'international', element: <IntlRequestsPage /> },
                              // All International (Paul, 2026-10-26) — same tier and gate as its
                              // sibling; the AIR resolver's per-op roles are the enforcement.
                              { path: 'all-international', element: <IntlAllRequestsPage /> },
                            ],
                          },
                        ],
                      },
                      // Short-lived standalone path, kept as a redirect so a tab opened while it
                      // existed does not 404.
                      {
                        path: 'shopify-orders',
                        element: <Navigate to="/staff/order-devices/shopify" replace />,
                      },
                    ],
                  },

                  // The old tab path, kept as a redirect so a bookmark from the few hours it
                  // existed lands on the page rather than a 404. Unguarded on purpose — it only
                  // bounces; the guard above then decides.
                  {
                    path: 'order-devices/demo',
                    element: (
                      <Navigate to="/staff/devices-fulfillment/create-demo-coupon" replace />
                    ),
                  },

                  // My Account — member-allowed (everyone sees these).
                  { path: 'enroll', element: <EnrollLaptopPage /> },
                  { path: 'onboarding', element: <OnboardingPage /> },
                  { path: 'my-devices', element: <AgentDevicesPage /> },

                  // Admin Users (root-only — upstream guard + defensive check)
                  {
                    path: 'admin-users',
                    element: <AdminRoleRoute requiredRole="root" />,
                    children: [{ index: true, element: <AdminUsersPage /> }],
                  },

                  // Stripe Products — the Mongo id → Stripe product map behind the
                  // Billing Console's Create Hardware Only Invoice tool (root-only).
                  {
                    path: 'optisigns-products',
                    element: <AdminRoleRoute requiredRole="root" />,
                    children: [{ index: true, element: <HardwareProductsPage /> }],
                  },

                  // OptiClock Time Dashboard (root-only — upstream guard + defensive check).
                  // A flat sibling of 'opticlock', so react-router ranks this more specific
                  // static path higher and the two guards stay independent.
                  {
                    path: 'opticlock/dashboard',
                    element: <AdminRoleRoute requiredRole="root" />,
                    children: [{ index: true, element: <TimeDashboardPage /> }],
                  },

                  // Activity Log (root-only — upstream guard + defensive check)
                  {
                    path: 'activity-log',
                    element: <AdminRoleRoute requiredRole="root" />,
                    children: [{ index: true, element: <ActivityLogPage /> }],
                  },

                  // Connection Audit (root-only — network-truth log, Administration section)
                  {
                    path: 'connection-audit',
                    element: <AdminRoleRoute requiredRole="root" />,
                    children: [{ index: true, element: <ConnectionAuditPage /> }],
                  },
                ],
              },
            ],
          },
        ],
      },

      // Fallback → role-aware landing
      { path: '*', element: <Navigate to="/staff" replace /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
